import {
  Circle,
  Info,
  IterationCcw,
  Lock,
  Plus,
  Shredder,
  Trash2,
  X,
} from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  getFactionName,
  getSkillDescription,
  getSkillName,
} from "../i18n/gameContent"
import { useI18n } from "../i18n/useI18n"
import { SKILL_MAP, SKILL_REGISTRY } from "../match/engine/content/skills"
import {
  ALLY_REQUIREMENT_DISCOUNT,
  computeSkillCostBreakdown,
} from "../match/engine/costs"
import type {
  CardColor,
  SkillDefinition,
  SkillId,
  SkillSelection,
} from "../schemas"
import { CostBadge, formatCostValue } from "./CostBadge"
import { FACTION_PALETTE } from "./cardTheme"
import { usePortalTarget } from "./PortalTarget"
import {
  AllyIcon,
  DoubleAllyIcon,
  ExhaustIcon,
  FACTION_ICON,
  SKILL_ICON,
} from "./SkillIcon"
import { SkillList } from "./SkillList"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  skills: SkillSelection[]
  baseCardCost?: number
  primaryColor: CardColor
  secondaryColor?: CardColor
  onChange: (skills: SkillSelection[]) => void
  forbiddenSkillIds?: ReadonlySet<string>
}

function isSkillAllowedForColors(
  skill: SkillDefinition,
  primary: CardColor,
  secondary?: CardColor,
) {
  if (primary === "colorless" || !secondary) return true
  return (
    skill.isColorless ||
    skill.affinity === primary ||
    skill.affinity === secondary
  )
}

function formatCost(value: number) {
  return formatCostValue(value)
}

function skillAffinityColor(skillId: SkillId, primary: CardColor): string {
  const def = SKILL_MAP.get(skillId)
  if (!def || def.isColorless) return FACTION_PALETTE.colorless.muted
  return (
    FACTION_PALETTE[def.affinity as CardColor]?.muted ??
    FACTION_PALETTE[primary].muted
  )
}

const FACTION_ORDER: CardColor[] = [
  "colorless",
  "red",
  "yellow",
  "green",
  "blue",
  "purple",
]

type SegmentOption = {
  key: string
  label: ReactNode
}

function previewSkillCost(
  selection: SkillSelection,
  baseCardCost: number,
): number {
  const breakdown = computeSkillCostBreakdown(
    [selection],
    SKILL_MAP,
    baseCardCost,
  )
  return (
    breakdown.skillCostDelta +
    breakdown.nonExhaustSurcharge +
    breakdown.selfDestructSurcharge
  )
}

function SegmentedControl({
  ariaLabel,
  options,
  value,
  onChange,
}: {
  ariaLabel: string
  options: SegmentOption[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <fieldset className="skill-picker-segmented" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          aria-pressed={option.key === value}
          className={`skill-picker-segment-btn${option.key === value ? " skill-picker-segment-btn-active" : ""}`}
          key={option.key}
          onClick={() => onChange(option.key)}
          type="button"
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  )
}

function cycleMode(
  sel: SkillSelection,
  def: SkillDefinition | undefined,
): SkillSelection {
  if (!def) return sel
  const modes: Array<SkillSelection["mode"]> = [undefined]
  if (def.nonExhaustSurcharge > 0) modes.push("non_exhausting")
  if (def.selfDestructSurcharge !== 0) modes.push("self_destruct")
  const idx = modes.indexOf(sel.mode)
  return { ...sel, mode: modes[(idx + 1) % modes.length] }
}

function cycleAlly(req: 1 | 2 | undefined): 1 | 2 | undefined {
  if (!req) return 1
  if (req === 1) return 2
  return undefined
}

// ---------------------------------------------------------------------------
// Skill picker popover
// ---------------------------------------------------------------------------

function SkillPickerPopover({
  available,
  baseCardCost = 0,
  selections,
  onAdd,
  onRemove,
  onClose,
  primary,
  secondary,
  anchorRect,
}: {
  available: readonly (typeof SKILL_REGISTRY)[number][]
  baseCardCost?: number
  selections: SkillSelection[]
  onAdd: (sel: SkillSelection) => void
  onRemove: (skillId: SkillId) => void
  onClose: () => void
  primary: CardColor
  secondary?: CardColor
  anchorRect: DOMRect
}) {
  const { t } = useI18n()
  const [filter, setFilter] = useState("")
  const [hovered, setHovered] = useState<SkillId | null>(
    available[0]?.id ?? null,
  )
  const [pendingMode, setPendingMode] = useState<
    SkillSelection["mode"] | undefined
  >(undefined)
  const [pendingAllyReq, setPendingAllyReq] = useState<1 | 2 | undefined>(
    undefined,
  )
  const popoverRef = useRef<HTMLDivElement>(null)
  const normalizedFilter = filter.trim().toLowerCase()
  const filteredAvailable = available.filter((def) => {
    if (!normalizedFilter) return true
    const factionName = getFactionName(
      t,
      def.isColorless ? "colorless" : def.affinity,
    )
    return [
      def.id,
      getSkillDescription(t, def.id as SkillId),
      factionName,
    ].some((field) => field.toLowerCase().includes(normalizedFilter))
  })
  const groupedAvailable = FACTION_ORDER.map((faction) => ({
    faction,
    skills: filteredAvailable.filter((def) =>
      def.isColorless ? faction === "colorless" : def.affinity === faction,
    ),
  })).filter((group) => group.skills.length > 0)
  const currentSelection = hovered
    ? selections.find((sel) => sel.skillId === hovered)
    : undefined

  // Reset pending state when hovered skill changes
  useEffect(() => {
    setPendingMode(currentSelection?.mode)
    setPendingAllyReq(currentSelection?.allyRequirement)
  }, [currentSelection])

  useEffect(() => {
    if (filteredAvailable.some((def) => def.id === hovered)) return
    setHovered(filteredAvailable[0]?.id ?? null)
  }, [filteredAvailable, hovered])

  // Close on Escape or outside click
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    const onOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    window.addEventListener("keydown", onKey, true)
    document.addEventListener("mousedown", onOutside, true)
    return () => {
      window.removeEventListener("keydown", onKey, true)
      document.removeEventListener("mousedown", onOutside, true)
    }
  }, [onClose])

  const browsed = hovered ? SKILL_MAP.get(hovered) : null
  const isAlreadyAdded = Boolean(currentSelection)
  const isBrowsedAllowed = browsed
    ? isSkillAllowedForColors(browsed, primary, secondary)
    : false
  const modeKey = pendingMode ?? "default"
  const allyKey =
    pendingAllyReq === 1 ? "ally" : pendingAllyReq === 2 ? "double" : "none"
  const previewSelection =
    browsed == null
      ? null
      : ({
          skillId: browsed.id,
          ...(pendingMode !== undefined ? { mode: pendingMode } : {}),
          ...(pendingAllyReq !== undefined
            ? { allyRequirement: pendingAllyReq }
            : {}),
        } satisfies SkillSelection)
  const totalPreviewCost =
    previewSelection == null
      ? 0
      : previewSkillCost(previewSelection, baseCardCost)
  const basePreviewCost =
    browsed == null
      ? 0
      : previewSkillCost({ skillId: browsed.id }, baseCardCost)
  const modeDelta =
    modeKey === "non_exhausting"
      ? (browsed?.nonExhaustSurcharge ?? 0)
      : modeKey === "self_destruct"
        ? (browsed?.selfDestructSurcharge ?? 0)
        : 0
  const allyDelta =
    pendingAllyReq && browsed?.supportsAllyRequirement
      ? -ALLY_REQUIREMENT_DISCOUNT[pendingAllyReq]
      : 0
  const totalPreviewTooltip = [
    t("skillsEditor.detail.previewBase", {
      cost: formatCost(basePreviewCost),
    }),
    modeDelta !== 0
      ? t("skillsEditor.detail.previewMode", {
          sign: modeDelta > 0 ? "+" : "-",
          cost: formatCost(Math.abs(modeDelta)),
        })
      : undefined,
    allyDelta !== 0
      ? t("skillsEditor.detail.previewAlly", {
          sign: allyDelta > 0 ? "+" : "-",
          cost: formatCost(Math.abs(allyDelta)),
        })
      : undefined,
  ]
    .filter(Boolean)
    .join("\n")
  const modeOptions = [
    {
      key: "default",
      label: (
        <span className="skill-picker-segment-option">
          <ExhaustIcon size={15} strokeWidth={2.1} />
        </span>
      ),
    },
    ...(browsed && browsed.nonExhaustSurcharge > 0
      ? [
          {
            key: "non_exhausting",
            label: (
              <span className="skill-picker-segment-option">
                <IterationCcw size={15} strokeWidth={2.1} />
              </span>
            ),
          },
        ]
      : []),
    ...(browsed
      ? [
          {
            key: "self_destruct",
            label: (
              <span className="skill-picker-segment-option">
                <Shredder size={15} strokeWidth={2.05} />
              </span>
            ),
          },
        ]
      : []),
  ]
  const showModeControl =
    !!browsed &&
    !browsed.isPassive &&
    (browsed.nonExhaustSurcharge > 0 || browsed.selfDestructSurcharge !== 0)
  const showAllyControl =
    !!browsed && !browsed.isPassive && browsed.supportsAllyRequirement

  const handleAdd = () => {
    if (!hovered || isAlreadyAdded || !browsed || !isBrowsedAllowed) return
    const sel: SkillSelection = { skillId: hovered }
    if (pendingMode !== undefined) sel.mode = pendingMode
    if (pendingAllyReq !== undefined) sel.allyRequirement = pendingAllyReq
    onAdd(sel)
  }

  const portalTarget = usePortalTarget()
  const popoverLeft = Math.min(anchorRect.left, window.innerWidth - 600)
  const popoverHeight = Math.min(352, window.innerHeight - 16)
  const openAbove = anchorRect.top > popoverHeight + 24

  return createPortal(
    <div
      className="skill-picker-popover"
      ref={popoverRef}
      style={{
        position: "fixed",
        top: openAbove ? anchorRect.top - 8 : anchorRect.bottom + 8,
        left: popoverLeft,
        zIndex: 9999,
        transform: openAbove ? "translateY(-100%)" : undefined,
      }}
    >
      {/* Left: skill list */}
      <div className="skill-picker-list">
        <div className="skill-picker-list-header">
          <p className="skill-picker-heading">{t("skillsEditor.heading")}</p>
          <input
            className="skill-picker-filter"
            onChange={(event) => setFilter(event.target.value)}
            placeholder={t("skillsEditor.filterPlaceholder")}
            type="text"
            value={filter}
          />
        </div>
        {groupedAvailable.map((group) => (
          <div className="skill-picker-group" key={group.faction}>
            <p className="skill-picker-group-heading">
              {getFactionName(t, group.faction)}
            </p>
            {group.skills.map((def) => {
              const Icon = SKILL_ICON[def.id as SkillId] ?? ExhaustIcon
              const isUsed = selections.some((sel) => sel.skillId === def.id)
              const isAllowed = isSkillAllowedForColors(def, primary, secondary)
              const color = skillAffinityColor(def.id as SkillId, primary)
              return (
                <div
                  className={[
                    "skill-picker-row",
                    hovered === def.id ? "skill-picker-row-active" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  key={def.id}
                >
                  <button
                    className={[
                      "skill-picker-item",
                      isUsed ? "skill-picker-item-used" : "",
                      !isAllowed ? "skill-picker-item-locked" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setHovered(def.id as SkillId)}
                    onClick={() =>
                      !isUsed &&
                      isAllowed &&
                      onAdd({ skillId: def.id as SkillId })
                    }
                    type="button"
                  >
                    <span className="skill-picker-item-icon" style={{ color }}>
                      <Icon size={14} strokeWidth={2} />
                    </span>
                    <span className="skill-picker-item-name">
                      {getSkillName(t, def.id as SkillId)}
                    </span>
                    {!isUsed && !isAllowed && (
                      <span
                        className="skill-picker-item-lock"
                        title={t("skillsEditor.locked")}
                      >
                        <Lock size={12} strokeWidth={2.1} />
                      </span>
                    )}
                    <CostBadge
                      className="skill-picker-item-cost"
                      cost={def.baseCostDelta}
                      size="m"
                      variant="subtle"
                    />
                  </button>
                  {isUsed && (
                    <button
                      aria-label={t("skillsEditor.removeAria", {
                        name: getSkillName(t, def.id as SkillId),
                      })}
                      className="skill-picker-item-remove"
                      onMouseEnter={() => setHovered(def.id as SkillId)}
                      onClick={() => onRemove(def.id as SkillId)}
                      type="button"
                    >
                      <Trash2 size={13} strokeWidth={2.1} />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        ))}
        {filteredAvailable.length === 0 && (
          <p className="skill-picker-empty-filter">
            {t("skillsEditor.emptyFilter")}
          </p>
        )}
      </div>

      {/* Right: description panel */}
      <div className="skill-picker-detail">
        {browsed ? (
          <>
            <div className="skill-picker-detail-header">
              {(() => {
                const Icon = SKILL_ICON[browsed.id as SkillId] ?? ExhaustIcon
                const color = skillAffinityColor(browsed.id as SkillId, primary)
                const AffinityIcon = browsed.isColorless
                  ? FACTION_ICON.colorless
                  : FACTION_ICON[browsed.affinity]
                return (
                  <>
                    <span
                      className="skill-picker-detail-icon"
                      style={{ color }}
                    >
                      <Icon size={22} strokeWidth={2} />
                    </span>
                    <div>
                      <p className="skill-picker-detail-name">
                        {getSkillName(t, browsed.id)}
                      </p>
                      <p className="skill-picker-detail-type">
                        {browsed.isPassive
                          ? t("skillsEditor.skillType.passive")
                          : t("skillsEditor.skillType.activatable")}
                        <span
                          className="skill-picker-detail-affinity"
                          style={{ color }}
                        >
                          <AffinityIcon size={12} strokeWidth={2.1} />
                          {getFactionName(
                            t,
                            browsed.isColorless
                              ? "colorless"
                              : browsed.affinity,
                          )}
                        </span>
                      </p>
                    </div>
                    <span title={totalPreviewTooltip}>
                      <CostBadge
                        cost={totalPreviewCost}
                        size="l"
                        variant="fill"
                      />
                    </span>
                  </>
                )
              })()}
            </div>
            <div className="skill-picker-detail-cost-row">
              <span className="skill-picker-detail-cost-label">
                {t("skillsEditor.detail.baseCost")}
              </span>
              <CostBadge cost={basePreviewCost} size="m" variant="subtle" />
            </div>

            <p className="skill-picker-detail-desc">
              {getSkillDescription(t, browsed.id)}
            </p>

            {/* Modifier options */}
            <div className="skill-picker-modifiers">
              {showModeControl && (
                <div className="skill-picker-control-block">
                  <p className="skill-picker-control-label">
                    {t("skillsEditor.useMode")}
                  </p>
                  <SegmentedControl
                    ariaLabel={t("skillsEditor.useMode")}
                    onChange={(nextKey) =>
                      setPendingMode(
                        nextKey === "default"
                          ? undefined
                          : (nextKey as SkillSelection["mode"]),
                      )
                    }
                    options={modeOptions}
                    value={modeKey}
                  />
                  <p className="skill-picker-control-help">
                    {modeKey === "non_exhausting" ? (
                      <>
                        <CostBadge
                          cost={browsed.nonExhaustSurcharge}
                          isRelative
                          size="m"
                          variant="subtle"
                        />{" "}
                        {t("skillsEditor.mode.nonExhaustingHelp")}
                      </>
                    ) : modeKey === "self_destruct" ? (
                      <>
                        <CostBadge
                          cost={browsed.selfDestructSurcharge}
                          isRelative
                          size="m"
                          variant="subtle"
                        />{" "}
                        {t("skillsEditor.mode.selfDestructHelp")}
                      </>
                    ) : (
                      t("skillsEditor.mode.defaultHelp")
                    )}
                  </p>
                </div>
              )}
              {showAllyControl && (
                <div className="skill-picker-control-block">
                  <p className="skill-picker-control-label">
                    {t("skillsEditor.allyRequirement")}
                  </p>
                  <SegmentedControl
                    ariaLabel={t("skillsEditor.allyRequirement")}
                    onChange={(nextKey) =>
                      setPendingAllyReq(
                        nextKey === "ally"
                          ? 1
                          : nextKey === "double"
                            ? 2
                            : undefined,
                      )
                    }
                    options={[
                      {
                        key: "none",
                        label: (
                          <span className="skill-picker-segment-option">
                            <Circle size={13} strokeWidth={2.1} />
                          </span>
                        ),
                      },
                      {
                        key: "ally",
                        label: (
                          <span
                            className="skill-picker-segment-option"
                            style={{
                              color: skillAffinityColor(browsed.id, primary),
                            }}
                          >
                            <AllyIcon size={14} strokeWidth={2.1} />
                          </span>
                        ),
                      },
                      {
                        key: "double",
                        label: (
                          <span
                            className="skill-picker-segment-option"
                            style={{
                              color: skillAffinityColor(browsed.id, primary),
                            }}
                          >
                            <DoubleAllyIcon size={14} strokeWidth={2.1} />
                          </span>
                        ),
                      },
                    ]}
                    value={allyKey}
                  />
                  <p className="skill-picker-control-help">
                    {allyKey === "ally" ? (
                      <>
                        <CostBadge
                          cost={-ALLY_REQUIREMENT_DISCOUNT[1]}
                          isRelative
                          size="m"
                          variant="subtle"
                        />{" "}
                        {t("skillsEditor.ally.oneHelp")}
                      </>
                    ) : allyKey === "double" ? (
                      <>
                        <CostBadge
                          cost={-ALLY_REQUIREMENT_DISCOUNT[2]}
                          isRelative
                          size="m"
                          variant="subtle"
                        />{" "}
                        {t("skillsEditor.ally.twoHelp")}
                      </>
                    ) : (
                      t("skillsEditor.ally.noneHelp")
                    )}
                  </p>
                </div>
              )}
            </div>

            <button
              className="skill-picker-add-btn"
              disabled={!isAlreadyAdded && !isBrowsedAllowed}
              onClick={() => {
                if (isAlreadyAdded && hovered) {
                  onRemove(hovered)
                  return
                }
                handleAdd()
              }}
              type="button"
            >
              {isAlreadyAdded ? (
                <Trash2 size={14} strokeWidth={2.3} />
              ) : (
                <Plus size={14} strokeWidth={2.5} />
              )}
              {isAlreadyAdded
                ? t("common.remove")
                : !isBrowsedAllowed
                  ? t("skillsEditor.wrongAffinity")
                  : t("skillsEditor.addSkill")}
            </button>
          </>
        ) : (
          <div className="skill-picker-detail-empty">
            <Info size={20} />
            <p>{t("skillsEditor.detail.empty")}</p>
          </div>
        )}
      </div>

      <button
        aria-label={t("skillsEditor.closeAria")}
        className="skill-picker-close"
        onClick={onClose}
        type="button"
      >
        <X size={18} />
      </button>
    </div>,
    portalTarget,
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SkillsEditor({
  skills,
  baseCardCost = 0,
  primaryColor,
  secondaryColor,
  onChange,
  forbiddenSkillIds,
}: Props) {
  const { t } = useI18n()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerAnchorRect, setPickerAnchorRect] = useState<DOMRect | null>(null)
  const available = forbiddenSkillIds
    ? SKILL_REGISTRY.filter((s) => !forbiddenSkillIds.has(s.id))
    : SKILL_REGISTRY
  const usedIds = skills.map((s) => s.skillId)

  const removeSkill = (index: number) => {
    onChange(skills.filter((_, i) => i !== index))
  }

  const addSkill = (sel: SkillSelection) => {
    if (usedIds.includes(sel.skillId)) return
    onChange([...skills, sel])
  }

  const updateSkill = (index: number, next: SkillSelection) => {
    onChange(skills.map((s, i) => (i === index ? next : s)))
  }

  return (
    <div className="skills-editor">
      <SkillList
        primaryColor={primaryColor}
        skills={skills}
        renderControls={({ definition, index, selection, skillName }) => {
          const hasModeCycle =
            !definition?.isPassive &&
            ((definition?.nonExhaustSurcharge ?? 0) > 0 ||
              (definition?.selfDestructSurcharge ?? 0) !== 0)
          const hasAllyCycle =
            !definition?.isPassive && !!definition?.supportsAllyRequirement

          return (
            <>
              {hasModeCycle && (
                <button
                  aria-label={t("skillsEditor.toggleModeAria", {
                    name: skillName,
                  })}
                  className="skills-modifier-btn"
                  onClick={() =>
                    updateSkill(index, cycleMode(selection, definition))
                  }
                  type="button"
                  title={
                    selection.mode === "non_exhausting"
                      ? t("cardFace.skill.nonExhausting")
                      : selection.mode === "self_destruct"
                        ? t("cardFace.skill.overload")
                        : t("skillsEditor.toggleMode.default")
                  }
                >
                  {selection.mode === "non_exhausting" ? (
                    <IterationCcw size={13} strokeWidth={2.1} />
                  ) : selection.mode === "self_destruct" ? (
                    <Shredder size={13} strokeWidth={2.05} />
                  ) : (
                    <ExhaustIcon size={13} strokeWidth={2.1} />
                  )}
                </button>
              )}
              {hasAllyCycle && (
                <button
                  aria-label={t("skillsEditor.toggleAllyAria", {
                    name: skillName,
                  })}
                  className="skills-modifier-btn"
                  onClick={() =>
                    updateSkill(index, {
                      ...selection,
                      allyRequirement: cycleAlly(selection.allyRequirement),
                    })
                  }
                  type="button"
                  title={
                    selection.allyRequirement === 1
                      ? t("skillsEditor.toggleAlly.one")
                      : selection.allyRequirement === 2
                        ? t("skillsEditor.toggleAlly.two")
                        : t("skillsEditor.toggleAlly.none")
                  }
                >
                  {selection.allyRequirement === undefined ? (
                    <Circle size={12} strokeWidth={2.1} />
                  ) : selection.allyRequirement === 1 ? (
                    <AllyIcon size={11} strokeWidth={2} />
                  ) : (
                    <DoubleAllyIcon size={11} strokeWidth={2} />
                  )}
                </button>
              )}
              <button
                aria-label={t("skillsEditor.removeAria", {
                  name: skillName,
                })}
                className="skills-editor-row-remove"
                onClick={() => removeSkill(index)}
                type="button"
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </>
          )
        }}
      />

      {/* Add skill placeholder */}
      <div className="skills-editor-add-wrap">
        <button
          className="skills-editor-add-btn"
          onClick={(e) => {
            setPickerAnchorRect(e.currentTarget.getBoundingClientRect())
            setPickerOpen(true)
          }}
          type="button"
        >
          <Plus size={13} strokeWidth={2.5} />
          {t("skillsEditor.addSkill")}
        </button>

        {pickerOpen && pickerAnchorRect && (
          <SkillPickerPopover
            available={available}
            baseCardCost={baseCardCost}
            selections={skills}
            onAdd={(sel) => {
              addSkill(sel)
              // Keep picker open so user can add multiple
            }}
            onRemove={(skillId) => {
              const index = skills.findIndex((sel) => sel.skillId === skillId)
              if (index >= 0) removeSkill(index)
            }}
            onClose={() => setPickerOpen(false)}
            primary={primaryColor}
            secondary={secondaryColor}
            anchorRect={pickerAnchorRect}
          />
        )}
      </div>
    </div>
  )
}
